(() => {
  'use strict';

  // Presentation only. Registry and catalog remain the sources of truth.
  // No recipient, purchaser, order, email, storage, or account data is requested.
  const speciesEntries = () => Object.values(window.INNERMON_SPECIES_RECORDS || {});
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  };
  // Secret Santa shopping lines only (owner-approved 2026-10-08). Dossier canon and the
  // gift-received page keep species.summary; a missing key falls back to it.
  const santaLines = {
    boss_dino: 'Big title. Loud opinions. Arms too short to reach the actual work.',
    peacock: 'Absent for the work. Front row for the applause.',
    parrot: 'Repeats what the boss just said. Calls it a fresh idea.',
    meerkat: 'Knows about the closed-door meeting before the door closes.',
    jellyfish: 'Drifts through every reorg. Consequences never quite land.',
    starfish: 'Means well. Helps anyway. Now there are three tasks.',
    turtle: 'Slow all day. Remarkably fast at 4:59 PM.',
    sloth: 'Your request is pending. It has been pending since Q2.',
    octopus: 'Eight arms. Zero drama. The problem is already solved.',
    puppy_intern: 'Thrilled to be here. Still waiting for a real task.',
    goldfish: 'Made a decision this morning. Has no memory of it.',
  };
  const santaLine = (species) => santaLines[species.internalKey] || species.summary;
  const dossierUrl = (species) => `/pages/species?view=lab-species-explorer#species=${encodeURIComponent(species.slug)}`;
  const imageUrl = (root, species) => {
    if (!/^[a-z0-9-]+\.webp$/.test(species.asset || '')) return '';
    const url = new URL(root.dataset.giftAssetReference, window.location.href);
    url.pathname = url.pathname.replace(/[^/]+$/, species.asset);
    url.search = '';
    url.hash = '';
    return url.href;
  };
  const makeSpeciesCard = (root, species) => {
    const card = element('li', 'innermon-gift__species');
    const link = element('a', 'innermon-gift__species-link');
    link.href = dossierUrl(species);
    const image = element('img');
    image.src = imageUrl(root, species);
    image.alt = '';
    image.width = 640;
    image.height = 640;
    image.loading = 'lazy';
    const media = element('div', 'innermon-gift__species-image');
    media.append(image);
    link.append(media, element('h3', '', species.name), element('p', 'innermon-gift__species-summary', species.summary), element('span', 'innermon-gift__card-action', 'Open Species File ↗'));
    card.append(link);
    return card;
  };

  const mountReceived = (root) => {
    const grid = root.querySelector('[data-gift-species-grid]');
    const species = speciesEntries();
    if (!grid || !species.length) return false;
    grid.replaceChildren(...species.map((record) => makeSpeciesCard(root, record)));
    root.querySelector('[data-gift-identification]')?.removeAttribute('hidden');
    root.querySelector('[data-gift-loading]')?.setAttribute('hidden', '');
    return true;
  };

  const mountSanta = (root) => {
    const groups = root.querySelector('[data-gift-product-groups]');
    if (!groups || typeof window.innermonHydrateCommerce !== 'function') return false;
    // Species groups start collapsed; CSS applies the collapse and shows the toggle on mobile only.
    const giftLabel = (count) => count + ' gift' + (count === 1 ? '' : 's');
    const setExpanded = (group, expanded) => {
      group.classList.toggle('is-collapsed', !expanded);
      const toggle = group.querySelector('.innermon-gift__group-toggle');
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.textContent = expanded ? 'Hide gifts' : 'Show ' + giftLabel(Number(group.dataset.giftCount) || 0);
    };
    const groupNodes = speciesEntries().map((species) => {
      const group = element('section', 'innermon-gift__product-group');
      group.hidden = true;
      group.dataset.giftSpecies = species.internalKey;
      const introduction = element('div', 'innermon-gift__product-intro');
      const heading = element('h2', '', species.name);
      heading.id = `${root.id}-${species.slug}`;
      heading.setAttribute('tabindex', '-1');
      group.setAttribute('aria-labelledby', heading.id);
      const link = element('a', 'innermon-gift__text-link', 'Meet this species ↗');
      link.href = dossierUrl(species);
      introduction.append(heading, element('p', 'innermon-gift__species-summary', santaLine(species)), link);
      const mount = element('div', 'innermon-species-merch');
      mount.dataset.innermonCommerceStatus = '';
      mount.dataset.commerceSpecies = species.internalKey;
      mount.dataset.commercePresentation = 'cards';
      mount.dataset.commerceContext = 'secret-santa';
      mount.id = `${root.id}-${species.slug}-gifts`;
      const toggle = element('button', 'innermon-gift__group-toggle');
      toggle.type = 'button';
      toggle.setAttribute('aria-controls', mount.id);
      toggle.addEventListener('click', () => setExpanded(group, group.classList.contains('is-collapsed')));
      introduction.append(toggle);
      group.classList.add('is-collapsed');
      group.append(introduction, mount);
      return group;
    });
    groups.replaceChildren(...groupNodes);
    // Reuse the exact product eligibility, native links, money, and card component.
    window.innermonHydrateCommerce();
    let visible = 0;
    const jumps = [];
    groupNodes.forEach((group) => {
      const mount = group.querySelector('[data-innermon-commerce-status]');
      if (!(Number(mount.dataset.productCount) > 0)) {
        group.remove();
        return;
      }
      group.hidden = false;
      visible += 1;
      const heading = group.querySelector('h2');
      const jump = element('a', '', heading.textContent);
      jump.href = `#${heading.id}`;
      jump.addEventListener('click', () => setExpanded(group, true));
      const item = element('li');
      item.append(jump);
      jumps.push(item);
    });
    const navigation = root.querySelector('[data-gift-species-nav]');
    if (navigation) {
      navigation.querySelector('ul').replaceChildren(...jumps);
      navigation.hidden = visible === 0;
    }
    root.querySelector('[data-gift-no-products]').hidden = visible > 0;
    root.querySelector('[data-gift-loading]')?.setAttribute('hidden', '');
    root.dataset.giftAvailableSpecies = String(visible);
    const catalog = speciesEntries().flatMap((species) => window.innermonCommerceCards.productsForSpecies(species.internalKey));
    const priority = ['species_file_mug', 'natural_tote', 'award_badge', 'graphic_tee'];
    const ranked = catalog.slice().sort((a, b) => priority.indexOf(a.product_type) - priority.indexOf(b.product_type));
    const picks = root.querySelector('[data-gift-picks]');
    const picksHeading = picks.querySelector('[data-gift-picks-heading]');
    const picksIntro = picks.querySelector('[data-gift-picks-intro]');
    const defaultPicksHeading = picksHeading.textContent;
    const pickGrid = root.querySelector('[data-gift-picks-grid]');
    // Picks show six cards at a time; "Show more" appends the next six for the current filter.
    const pickStep = 6;
    let pickCards = [];
    const morePicks = element('button', 'innermon-gift__more');
    morePicks.type = 'button';
    pickGrid.after(morePicks);
    const updateMorePicks = () => {
      const remaining = pickCards.length - pickGrid.children.length;
      morePicks.hidden = remaining <= 0;
      const next = Math.min(pickStep, remaining);
      morePicks.textContent = 'Show ' + next + ' more gift' + (next === 1 ? '' : 's');
    };
    morePicks.addEventListener('click', () => {
      const shown = pickGrid.children.length;
      pickGrid.insertAdjacentHTML('beforeend', pickCards.slice(shown, shown + pickStep).join(''));
      updateMorePicks();
      pickGrid.children[shown]?.querySelector('a')?.focus();
    });
    const filters = root.querySelector('[data-gift-filters]');
    const currency = root.dataset.currency || 'USD';
    filters.querySelectorAll('[data-gift-budget]').forEach((button) => {
      if (button.dataset.giftBudget !== 'all') {
        const maximum = Number(button.dataset.giftBudget);
        button.textContent = 'Under ' + (currency === 'USD' ? '$' : currency + ' ') + maximum / 100;
        // Compare the current eligible catalog, never a historical product count.
        button.hidden = ranked.filter((product) => Number.isFinite(product.price_amount) && product.price_amount < maximum).length === ranked.length;
      }
      button.addEventListener('click', () => applyBudget(button.dataset.giftBudget));
    });
    function applyBudget(value) {
      const maximum = value === 'all' ? Infinity : Number(value);
      const matches = (product) => value === 'all' || (Number.isFinite(product.price_amount) && product.price_amount < maximum);
      const current = ranked.filter(matches);
      const activeFilter = filters.querySelector('[data-gift-budget="' + value + '"]');
      picksHeading.textContent = value === 'all' ? defaultPicksHeading : activeFilter.textContent;
      picksIntro.hidden = value !== 'all';
      pickCards = current.map((product) => window.innermonCommerceCards.productCard(product)).filter(Boolean);
      pickGrid.innerHTML = pickCards.slice(0, pickStep).join('');
      updateMorePicks();
      picks.hidden = !pickGrid.children.length;
      let visibleGroups = 0;
      groupNodes.filter((group) => group.isConnected).forEach((group) => {
        let count = 0;
        group.querySelectorAll('[data-product-price]').forEach((card) => {
          card.hidden = value !== 'all' && (!card.dataset.productPrice || Number(card.dataset.productPrice) >= maximum);
          if (!card.hidden) count++;
        });
        group.querySelectorAll('.innermon-species-merch__fallbacks').forEach((fallback) => { fallback.hidden = value !== 'all'; if (!fallback.hidden) count++; });
        group.hidden = !count;
        group.dataset.giftCount = String(count);
        setExpanded(group, !group.classList.contains('is-collapsed'));
        if (count) visibleGroups++;
        const jump = navigation?.querySelector('a[href="#' + group.querySelector('h2').id + '"]');
        if (jump) jump.closest('li').hidden = !count;
      });
      if (navigation) navigation.hidden = !visibleGroups;
      root.querySelector('[data-gift-no-products]').hidden = !!visibleGroups;
      root.querySelector('[data-gift-filter-status]').textContent = current.length + ' gift' + (current.length === 1 ? '' : 's') + ' · ' + currency + ' · before tax';
      filters.querySelectorAll('button').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.giftBudget === value)));
    }
    filters.hidden = !catalog.length;
    applyBudget('all');
    // Arriving with a species anchor (shared link or jump) opens that group.
    const target = groupNodes.find((group) => group.isConnected && location.hash === '#' + group.querySelector('h2').id);
    if (target) setExpanded(target, true);
    return true;
  };

  const mount = () => document.querySelectorAll('[data-innermon-gift-experience]').forEach((root) => {
    if (root.dataset.giftReady === 'true') return;
    if (!speciesEntries().length) return;
    const mounted = root.dataset.innermonGiftExperience === 'received'
      ? mountReceived(root)
      : root.dataset.innermonGiftExperience === 'santa' && mountSanta(root);
    if (mounted) root.dataset.giftReady = 'true';
  });

  // `interactive` is also the normal execution state of deferred scripts. Wait
  // for the later footer commerce script before mounting the catalog surface.
  if (document.readyState === 'complete') mount();
  else document.addEventListener('DOMContentLoaded', mount, { once: true });
  document.addEventListener('shopify:section:load', mount);
})();
